import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { CountModal } from "@/Component/CountModal";
import { SelectCachedOptions } from "@/Component/SelectCachedOptions";
import { LabelValue } from "@/Component/Value";
import { useMakkahPilgrimGroupNameMappingsMutation, useOneMakkahPilgrimGroupNameMappingQuery, usePilgrimGroupNames, useUpdateMakkahPilgrimGroupNameMappingMutation } from "@/services/basicData";
import { MODES } from "@/utils/constants";
import { handleFormErrors } from "@/utils/helpers";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useEffect } from "react";
import { Button, Stack } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";
import { toast } from "react-toastify";
import { Container, Spinner } from "reactstrap";

export const MakkahPilgrimGroupNameMappingFrom = () => {
    const { t } = useTranslation();
    const { action } = useParams();

    const title = {
        create: t("CREATE_PILGRIM_GROUP_NAME_MAPPINGS"),
        edit: t("EDIT_PILGRIM_GROUP_NAME_MAPPINGS"),
        view: t("VIEW_PILGRIM_GROUP_NAME_MAPPINGS"),
    }

    return (
        <Stack gap={2}>
            <PageHeader title={title[action]} />
            <Container fluid>
                <MakkahPilgrimGroupNameMappingCRUD />
            </Container>
        </Stack>
    )
}

const backTo = window.length > 1 ? -1 : "/app/makkah/basic-data/makkah-pilgrim-group-name-mappings";

const MakkahPilgrimGroupNameMappingCRUD = () => {
    const { t } = useTranslation();
    const { getLocalizedText } = useLocalizedText();
    const { action } = useParams();
    const isView = action === MODES.view;
    const isUpdate = action === MODES.edit;

    // ===================
    // Queries
    // ===================
    const { mutate: createMakkahPilgrimGroupNameMappings, isPending: isLoadingMakkahPilgrimGroupNameMappings } = useMakkahPilgrimGroupNameMappingsMutation();
    const { mutate: updateMakkahPilgrimGroupNameMapping, isPending: isLoadingUpdateMakkahPilgrimGroupNameMapping } = useUpdateMakkahPilgrimGroupNameMappingMutation();
    const { data: oneMakkahPilgrimGroupNameMapping, isLoading: isLoadingOneMakkahPilgrimGroupNameMapping } = useOneMakkahPilgrimGroupNameMappingQuery();
    const oneMakkahPilgrimGroupNameMappingData = oneMakkahPilgrimGroupNameMapping?.data?.data;
    const form = useForm({ disabled: isLoadingMakkahPilgrimGroupNameMappings || isLoadingOneMakkahPilgrimGroupNameMapping });
    const { handleSubmit, control, setValue, formState: { errors } } = form;
    const [mission_id, company_id] = form.watch(["mission_id", "company_id"]);
    const { data: pilgrimGroupNames, isLoading: isLoadingPilgrimGroupNames } = usePilgrimGroupNames({ company_id, mission_id });
    const rawGroupNamesOptions = pilgrimGroupNames?.data?.data.map((m) => ({
        value: m.raw_group_name,
        label: <LabelValue.Value value={getLocalizedText(m.raw_group_name_ar, m.raw_group_name_en)} />,
    })) ?? [];

    const onSubmit = (data) => {
        if (isUpdate) {
            updateMakkahPilgrimGroupNameMapping(data, {
                onError: (error) => {
                    toast.error(error?.response?.data?.message || t("UPDATE_PILGRIM_GROUP_NAME_MAPPINGS_FAILED"));
                    handleFormErrors(error, form.setError, t, "UPDATE_PILGRIM_GROUP_NAME_MAPPINGS_FAILED");
                },
            });

            return;
        }

        createMakkahPilgrimGroupNameMappings(data, {
            onError: (error) => {
                toast.error(error?.response?.data?.message || t("CREATE_PILGRIM_GROUP_NAME_MAPPINGS_FAILED"));
                handleFormErrors(error, form.setError, t, "CREATE_PILGRIM_GROUP_NAME_MAPPINGS_FAILED");
            },
        });
    }

    useEffect(() => {
        form.reset(oneMakkahPilgrimGroupNameMappingData);
    }, [oneMakkahPilgrimGroupNameMapping]);

    const ctrls = [
        {
            labelProps: { children: t("ORGANIZER"), className: "required" },
            controllerProps: {
                name: "mission_id",
                rules: { required: t("FIELD_REQUIRED") },
                render: ({ field, fieldState: { error } }) => (
                    <SelectCachedOptions
                        {...field}
                        onChange={(value) => {
                            field.onChange(value);
                            setValue("company_id", null);
                            setValue("mapped_group_name", "");
                            setValue("raw_group_names", []);
                        }}
                        showSearch
                        id="mission_id"
                        placeholder={t("ORGANIZER")}
                        status={error ? "error" : undefined}
                        optionKey="missions"
                        options={(m) => ({
                            value: m.id,
                            label: <LabelValue.Value value={[m.id, getLocalizedText(m.name_ar, m.name_en)].join(" - ")} />,
                        })}
                    />
                ),
            },
            renderValue: (value) => <SelectCachedOptions.Display
                value={value}
                optionKey="missions"
                campareBy="id"
                render={(option) => [option?.id, getLocalizedText(option?.name_ar, option?.name_en) || option?.id].filter(Boolean).join(" - ")}
            />,
        },
        {
            labelProps: { children: t("AFFILIATED_ORGANIZER"), },
            controllerProps: {
                name: "company_id",
                render: ({ field, fieldState: { error } }) => (
                    <SelectCachedOptions
                        {...field}
                        filter={(m) => {
                            return String(m.mission_id) === String(mission_id)
                        }}
                        showSearch
                        allowClear
                        id="company_id"
                        placeholder={t("AFFILIATED_ORGANIZER")}
                        status={error ? "error" : undefined}
                        optionKey="companies"
                        options={(m) => ({
                            value: m.id,
                            label: <LabelValue.Value value={[m.id, getLocalizedText(m.name_ar, m.name_en)].filter(Boolean).join(" - ")} />,
                        })}
                    />
                ),
            },

            renderValue: (value) => <SelectCachedOptions.Display
                value={value}
                optionKey="companies"
                campareBy="id"
                render={(option) => [option?.id, getLocalizedText(option?.name_ar, option?.name_en) || option?.id].filter(Boolean).join(" - ")}
            />,
        },
        {
            labelProps: { children: t("MAPPED_GROUP_NAME"), className: "required" },
            controllerProps: {
                name: "mapped_group_name",
                rules: { required: t("FIELD_REQUIRED") },
                render: ({ field, fieldState: { error } }) => (
                    <AntInput
                        {...field}
                        id="mapped_group_name"
                        name="mapped_group_name"
                        placeholder={t("MAPPED_GROUP_NAME")}
                        status={error ? "error" : undefined}
                    />
                ),
            },
        },
        {
            labelProps: { children: t("RAW_GROUP_NAMES"), className: "required" },
            controllerProps: {
                name: "raw_group_names",
                rules: { required: t("FIELD_REQUIRED") },
                render: ({ field, fieldState: { error } }) => (
                    <>

                        <style>{`
  .ant-select-selector * {
    min-height: 40px !important;
    display: flex !important;
    align-items: center !important;
  }
`}</style>
                        <AntSelect

                            {...field}
                            mode="multiple"
                            loading={isLoadingPilgrimGroupNames}
                            disabled={isLoadingPilgrimGroupNames}
                            showSearch
                            id="raw_group_names"
                            placeholder={t("RAW_GROUP_NAMES")}
                            status={error ? "error" : undefined}
                            options={rawGroupNamesOptions}
                        />
                    </>
                ),
            },

            renderValue: (value) => <CountModal
                count={value?.length}
                modal={{
                    props: {
                        size: "lg",
                        centered: true,
                        scrollable: true,
                    },
                    header: {
                        children: t("ITEMS_COUNT"),
                    },
                    footer: {},
                    body: {
                        children: (
                            <ul className="p-0 d-flex flex-column gap-2">
                                {value?.map((item) => (
                                    <li key={item.id} className="border py-2 px-1 rounded-2">
                                        <LabelValue.Value
                                            value={item}
                                        />
                                    </li>
                                ))}
                            </ul>
                        ),
                    },
                    props: {},
                }}
            >
                {value?.[0]}
            </CountModal>
        },
    ]

    return (
        <div>
            <form onSubmit={handleSubmit(onSubmit)}>
                <Controllers
                    isViewMode={isView}
                    controllers={ctrls.map((c) => ({
                        ...c,
                        name: c.controllerProps.name,
                        controllerProps: { ...c.controllerProps, control, errors },
                    }))}
                    watch={form.watch}
                />

                {!isView && <ActionsFooter>
                    <Button className="btn btn-primary" type="submit" disabled={isLoadingMakkahPilgrimGroupNameMappings || isLoadingUpdateMakkahPilgrimGroupNameMapping}>
                        {(isLoadingMakkahPilgrimGroupNameMappings || isLoadingUpdateMakkahPilgrimGroupNameMapping) && <Spinner size="sm" />}
                        {t("SAVE")}
                    </Button>
                    <Link to={backTo} style={{ pointerEvents: isLoadingMakkahPilgrimGroupNameMappings || isLoadingUpdateMakkahPilgrimGroupNameMapping ? "none" : "auto" }} className="btn btn-danger" type="button" disabled={isLoadingMakkahPilgrimGroupNameMappings || isLoadingUpdateMakkahPilgrimGroupNameMapping}>{t("CANCEL")}</Link>
                </ActionsFooter>}
            </form>
        </div>
    )
}